import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories, faqs } from '@/mocks/comics';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const featured = comics.filter((c) => c.featured);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % featured.length);
  }, [featured.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + featured.length) % featured.length);
  }, [featured.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '叮咚漫画网 - 韩漫免费阅读',
          description: '叮咚漫画网提供韩漫免费阅读服务，包括韩国漫画在线和高清韩漫大全。',
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[600px] overflow-hidden" aria-label="热门推荐">
            {featured.map((comic, index) => (
              <div
                key={comic.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/40 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent"></div>
              </div>
            ))}

            <div className="absolute inset-0 z-20 flex items-center">
              <div className="max-w-7xl mx-auto w-full px-4 md:px-6">
                <div className="max-w-lg">
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500 text-background-50 rounded-full mb-4">
                    热门推荐
                  </span>
                  <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 font-heading">
                    {featured[currentSlide]?.title}
                  </h1>
                  <p className="text-sm md:text-base text-background-50/80 mb-6 line-clamp-2">
                    {featured[currentSlide]?.description}
                  </p>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-1 text-accent-400">
                      <i className="ri-star-fill"></i>
                      <span className="text-background-50 font-semibold">{featured[currentSlide]?.rating}</span>
                    </div>
                    <span className="text-background-50/60 text-sm">
                      <i className="ri-eye-line mr-1"></i>{featured[currentSlide]?.views}
                    </span>
                    <span className="text-background-50/60 text-sm">
                      共{featured[currentSlide]?.chapters}话
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-md ${
                      featured[currentSlide]?.status === '连载中'
                        ? 'bg-primary-500/80 text-background-50'
                        : 'bg-secondary-500/80 text-background-50'
                    }`}>
                      {featured[currentSlide]?.status}
                    </span>
                  </div>
                  <Link
                    to={`/article/${featured[currentSlide]?.id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-read-line"></i>
                    开始阅读
                  </Link>
                </div>
              </div>
            </div>

            <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
              {featured.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    index === currentSlide
                      ? 'bg-primary-500 w-6'
                      : 'bg-background-50/40 hover:bg-background-50/70'
                  }`}
                  aria-label={`切换到第${index + 1}个推荐`}
                />
              ))}
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 transition-colors cursor-pointer"
              aria-label="上一个"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 transition-colors cursor-pointer"
              aria-label="下一个"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </section>

          {/* Category Navigation */}
          <section className="py-8 md:py-10 border-b border-background-200" aria-label="分类导航">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 bg-background-100 hover:bg-primary-100 hover:text-primary-700 rounded-full transition-colors whitespace-nowrap flex-shrink-0 cursor-pointer"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Hot Ranking */}
          <section className="py-10 md:py-14" aria-label="热门排行">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  热门排行
                </h2>
                <Link
                  to="/category/all"
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {comics.slice(0, 6).map((comic, idx) => (
                  <div key={comic.id} className="relative">
                    <span className="absolute -top-2 -left-2 z-10 w-6 h-6 flex items-center justify-center bg-primary-500 text-background-50 text-xs font-bold rounded-full">
                      {idx + 1}
                    </span>
                    <ComicCard {...comic} />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="py-10 md:py-14 bg-background-100" aria-label="最新更新">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  最新更新
                </h2>
                <Link
                  to="/category/all"
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {[...comics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 6).map((comic) => (
                  <div key={comic.id} className="relative">
                    <span className="absolute top-2 left-2 z-10 px-1.5 py-0.5 text-xs bg-primary-500 text-background-50 rounded-md">
                      NEW
                    </span>
                    <ComicCard {...comic} />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Banner */}
          <section className="py-10 md:py-14" aria-label="特色功能">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4 p-6 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">高清画质</h3>
                    <p className="text-xs text-foreground-600">所有漫画资源均为高清画质，优化加载速度，畅享极致的韩漫免费阅读体验。</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                    <i className="ri-flashlight-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">极速更新</h3>
                    <p className="text-xs text-foreground-600">第一时间同步韩国漫画在线最新章节，热门连载作品每周稳定更新。</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg">
                    <i className="ri-smartphone-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">多端适配</h3>
                    <p className="text-xs text-foreground-600">完美适配电脑、平板和手机，随时随地畅享高清韩漫大全资源。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-10 md:py-14 bg-background-100" aria-label="常见问题">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8 font-heading">
                常见问题
              </h2>
              <script type="application/ld+json">
                {JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: faqs.slice(0, 4).map((faq) => ({
                    '@type': 'Question',
                    name: faq.question,
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: faq.answer,
                    },
                  })),
                })}
              </script>
              <div className="space-y-3">
                {faqs.slice(0, 4).map((faq, index) => (
                  <details key={index} className="group bg-background-50 rounded-lg border border-background-200">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h3 className="text-sm font-medium text-foreground-900 font-heading pr-4">
                        {faq.question}
                      </h3>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}